export const CANCELLATION_REQUEST_REASONS = [
  "BEFORE_EFFECT_DATE",
  "FIRST_30_DAYS",
  "POLICY_ANNULMENT",
  "RENEWAL",
] as const;

export type CancellationRequestReason =
  (typeof CANCELLATION_REQUEST_REASONS)[number];

export const CANCELLATION_REQUEST_STATUSES = [
  "DRAFT",
  "SIGNATURE_PENDING",
  "SIGNED",
  // Italian recesso (D.lgs. 209/2025): created without signature, awaiting ops
  // confirmation. Reachable to COMPLETED just like SIGNED.
  "RECESSO_PENDING",
  "COMPLETED",
  "REJECTED",
] as const;

export type CancellationRequestStatus =
  (typeof CANCELLATION_REQUEST_STATUSES)[number];

// Administrative (Tipo 2/3) cancellations don't have a real status — they're
// either canceled or not. We surface them through the same filter pill using
// this synthetic token, which the backend translates into "include Tipo 2/3
// rows" (see CancellationRequestController::STATUS_ADMIN_CANCELED).
export const ADMIN_CANCELED_STATUS = "CANCELED" as const;

export type CancellationFilterStatus =
  | CancellationRequestStatus
  | typeof ADMIN_CANCELED_STATUS;

export type CancellationKind = "TIPO_1" | "TIPO_2_3";

export const CANCELLATION_KIND_LABELS: Record<CancellationKind, string> = {
  TIPO_1: "Cliente (Tipo 1)",
  TIPO_2_3: "Administrativa (Tipo 2/3)",
};

export const CANCELLATION_REASON_LABELS: Record<
  CancellationRequestReason,
  string
> = {
  BEFORE_EFFECT_DATE: "Baja antes de Fecha de Efecto",
  FIRST_30_DAYS: "Baja en los primeros 30 días",
  POLICY_ANNULMENT: "Anulación de la Póliza",
  RENEWAL: "Renovación",
};

export const CANCELLATION_STATUS_LABELS: Record<
  CancellationRequestStatus,
  string
> = {
  DRAFT: "Borrador",
  SIGNATURE_PENDING: "Esperando firma",
  SIGNED: "Firmada",
  RECESSO_PENDING: "Recesso pendiente",
  COMPLETED: "Completada",
  REJECTED: "Revocada",
};

export type CancellationRequestDto = {
  kind: CancellationKind;
  id: string;
  leadInternalId: number;
  leadUlid: string;
  policyNumber: string;
  tomadorFirstName: string;
  tomadorLastName: string;
  // Tipo 1 enum value; null for Tipo 2/3 (legacy free-text reason lives in reasonLabel)
  reason: CancellationRequestReason | null;
  reasonLabel: string;
  // For Tipo 2/3 the request date may be missing in legacy rows
  requestDate: string | null;
  communicationDate: string | null;
  effectiveCancellationDate: string | null;
  pdfUrlUnsigned: string | null;
  pdfUrlSigned: string | null;
  viafirmaId: string | null;
  viafirmaStatus: string | null;
  status: CancellationFilterStatus;
  // Legacy cancellation_type ("Standard cancellation", "Withdrawal", "Not effective") for Tipo 2/3
  cancellationType: string | null;
  createdAt: string | null;
  signedAt: string | null;
  completedAt: string | null;
  // Timestamp of the last "send to AXA" email (Tipo 1 SIGNED requests); null if never sent
  insurerNotifiedAt: string | null;
  createdByUserId: number | null;
};

export type CancellationRequestListResponse = {
  items: CancellationRequestDto[];
  total: number;
  page: number;
  pageSize: number;
};
